import { Button } from '@zillow/constellation-10';
import { useEffect, useState } from 'react';
import { UpsellTrackingIdType, UpsellType } from '@zg-rentals/ts-rins-client';
import { useUpsellWebApiHandler_viewUpsell } from '@zg-rentals/ts-rins-client-react-query';

import UpsellModal from '../UpsellModal';
import { UpsellEntryPointType } from '../../types';
import { hasUpsellBeenDismissed, useCreateUpsell } from '../../hooks/hooks';
import type { TrackClickstreamEventFn } from '../../utils/ClickstreamEvents';
import { VIEW_LEASE_UPSELL } from '../../utils/ClickstreamEvents';

type PostLeaseSigningUpsellProps = {
  leaseId: string;
  nonUpsellButton: JSX.Element;
  onClose: () => void;
  onUpsellLoadingComplete: (shouldDisplayUpsell: boolean) => void;
  trackClickstreamEvent: TrackClickstreamEventFn;
};

const TRACKING_ID_TYPE = UpsellTrackingIdType.LEASE_ID;
const UPSELL_TYPE = UpsellType.LEASE_SIGNED_FLOW;
const UPSELL_ENTRY_POINT_TYPE = UpsellEntryPointType.LEASES_COMPLETED_SIGNING;

const PostLeaseSigningUpsell = ({
  leaseId,
  nonUpsellButton,
  onClose,
  onUpsellLoadingComplete,
  trackClickstreamEvent,
}: PostLeaseSigningUpsellProps) => {
  const [openDialog, setOpenDialog] = useState(false);
  const [upsellInfo, setUpsellInfo] = useState({
    isInsuranceRequiredForLease: false,
    isInsuranceRequiredForListing: false,
    shouldDisplayUpsell: false,
    monthlyPriceEstimate: '',
    redirectUrl: '',
    upsellId: '',
    zip: '',
  });

  const { mutate: viewUpsellMutation } = useUpsellWebApiHandler_viewUpsell();

  const { createUpsell: createUpsellMutation, isLoading } = useCreateUpsell({
    onSuccess: (data) => {
      const { id: upsellId, shouldDisplayUpsell, url: redirectUrl } = data.upsell;

      if (shouldDisplayUpsell) {
        setUpsellInfo({
          upsellId,
          redirectUrl,
          ...data.upsell,
        });
      }

      onUpsellLoadingComplete(shouldDisplayUpsell);
    },
    onError: () => {
      onUpsellLoadingComplete(false);
    },
  });

  useEffect(() => {
    if (!hasUpsellBeenDismissed(UPSELL_TYPE, TRACKING_ID_TYPE, leaseId)) {
      createUpsellMutation(UPSELL_TYPE, TRACKING_ID_TYPE, leaseId);
    } else {
      onUpsellLoadingComplete(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [leaseId]);

  const onUpsellView = () => {
    trackClickstreamEvent(VIEW_LEASE_UPSELL(UPSELL_ENTRY_POINT_TYPE));
    viewUpsellMutation({ postData: { upsellId: upsellInfo.upsellId } });
  };

  const showUpsellIfEligible = async () => {
    await onUpsellView();
    setOpenDialog(true);
  };

  const closeUpsellModal = () => {
    setOpenDialog(false);
    return onClose();
  };

  if (isLoading || !upsellInfo.shouldDisplayUpsell) {
    return nonUpsellButton;
  }

  return (
    <>
      <UpsellModal
        isOpen={openDialog}
        onClose={closeUpsellModal}
        upsellEntryPointType={UPSELL_ENTRY_POINT_TYPE}
        upsellInfo={upsellInfo}
        trackClickstreamEvent={trackClickstreamEvent}
      />
      <Button data-testid="post-lease-signing-show-upsell-btn" emphasis="filled" onClick={showUpsellIfEligible}>
        Next
      </Button>
    </>
  );
};

export default PostLeaseSigningUpsell;
